import type { NotificationPriority, SkillMessage } from '../../../notifications/types';
import { formatMessageAge } from '../../../chat/skillMessageSession';
import styles from './SkillMessageRow.module.css';

/** Only urgent and high are labelled; normal and low look the same. */
const PRIORITY_LABELS: Partial<Record<NotificationPriority, string>> = { urgent: 'Urgent', high: 'High' };

interface SkillMessageRowProps {
  readonly message: SkillMessage;
  readonly onOpen: (message: SkillMessage) => void;
  /** Injected for tests; defaults to the current time. */
  readonly now?: number;
}

/**
 * One skill text message in the Actions tab. Opening it starts the message's
 * own chat session, where its quick replies are offered as pills.
 *
 * Unread is shown by a dot, a bold title and a tint together, plus a
 * screen-reader label, so state never rests on colour alone.
 */
export function SkillMessageRow({ message, onOpen, now }: Readonly<SkillMessageRowProps>) {
  const unread = !message.read;
  const replies = message.quickReplies.length;
  const age = formatMessageAge(message.createdAtUtc, now);
  const priorityLabel = PRIORITY_LABELS[message.priority];
  const className = [styles.row, unread ? styles.unread : ''].filter(Boolean).join(' ');

  return (
    <button type="button" className={className} onClick={() => onOpen(message)}>
      <span className={styles.dotCell} aria-hidden="true">
        {unread && <span className={styles.dot} />}
      </span>
      <span className={styles.content}>
        {unread && <span className={styles.srOnly}>Unread. </span>}
        <span className={styles.top}>
          <span className={styles.from}>{message.skillName || 'Skill'}</span>
          {age && <span className={styles.time}>{age}</span>}
        </span>
        <span className={styles.title}>{message.title}</span>
        {message.body && <span className={styles.body}>{message.body}</span>}
        {(priorityLabel || replies > 0) && (
          <span className={styles.meta}>
            {priorityLabel && (
              <span
                className={`${styles.priority} ${message.priority === 'urgent' ? styles.urgent : styles.high}`}
              >
                {priorityLabel}
              </span>
            )}
            {replies > 0 && (
              <span className={styles.replies}>
                <svg viewBox="0 0 16 16" aria-hidden="true" className={styles.replyIcon}>
                  <path d="M6 4L2.5 7.5 6 11" />
                  <path d="M2.5 7.5H9a4.5 4.5 0 014.5 4.5v.5" />
                </svg>
                {replies === 1 ? '1 quick reply' : `${replies} quick replies`}
              </span>
            )}
          </span>
        )}
      </span>
    </button>
  );
}
